import { useEffect, useRef } from 'react'

type EventApi<T> = T extends undefined
  ? {
      emit(): void
      useListen(callback: () => void): void
      listen(callback: () => void): () => void
    }
  : {
      emit(detail: T): void
      useListen(callback: (detail: T) => void): void
      listen(callback: (detail: T) => void): () => void
    }

interface EventOptions {
  target?: EventTarget
}

export function createEvent<T = undefined>(
  eventName: string,
  options: EventOptions = {},
): EventApi<T> {
  const target =
    options.target ??
    (typeof document === 'undefined' ? new EventTarget() : document)

  function emit(...args: T extends undefined ? [] : [T]): void {
    const detail = args[0] as T
    target.dispatchEvent(new CustomEvent<T>(eventName, { detail }))
  }

  function listen(callback: (detail: T) => void): () => void {
    if (typeof document === 'undefined') return () => {}

    const handler = (e: Event) => {
      callback((e as CustomEvent<T>).detail)
    }
    target.addEventListener(eventName, handler)
    return () => {
      target.removeEventListener(eventName, handler)
    }
  }

  function useListen(callback: (detail: T) => void): void {
    const callbackRef = useRef(callback)

    useEffect(() => {
      callbackRef.current = callback
    }, [callback])

    useEffect(() => {
      return listen((detail) => {
        callbackRef.current(detail)
      })
    }, [])
  }

  return { emit, useListen, listen } as EventApi<T>
}
